import React, { useEffect, useState } from "react";
import { Button, Skeleton, Typography, Box, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Profileinformation from "../../Cards/Profile";
import EnergyUsage from "../../Cards/EnergyUsage";
import CurrentBilling from "../../Cards/CurrentBilling";
import GrantInformationAndHistory from "../../Cards/GrantInformationAndHistory";
import PaymentTransactionHistory from "../../Cards/PaymentTransactionHistory";
import {
  getCurrentBillingApi,
  getProfileInfoApi,
  getEnergyUsageApi,
  getGrantInfoApi,
  getBillPaymentHistoryApi,
  getEmergencyHoldApi,
  getEmergencyHoldStatusApi,
  getAgencyListApi,
} from "services/energyAssistance/energyAssistance.thunks";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import ReconnectModal from "../../Modals/ReconnectModal";
import {
  getAccountLookup,
  getProfileInfo,
  getEmergencyHoldStatus,
} from "selectors/energyAssistance.selectors";
import { initialState } from "container/energyAssistance/store/initialState";
import { AutocompleteInput, ModalPopup } from "_components";
import { alertActions } from "_store";
import { ERR_MSG } from "constants/labels";
import PledgeAmountModal from "../../Modals/PledgeAmountModal";
import { getPledgeEmergencyHoldState } from "container/energyAssistance/utils/pledgeEmergencyHoldRules";

// AccountNumber, OpenBalances, SecurityDepositAmountAssessedInTotal, and
// SecurityDepositOpenAmount are passed down from EnergyAssistance.jsx (the
// account-lookup row data). They're forwarded to CurrentBilling below since
// that's the card that displays them, and that data doesn't come from any
// of the APIs fetched in this component.
const EnergyAssistanceDetails = ({
  AccountNumber,
  OpenBalances,
  SecurityDepositAmountAssessedInTotal,
  SecurityDepositOpenAmount,
}) => {
  const authUser = useSelector((x) => x.auth?.value);
  const user = authUser?.Data;
  const userAccess = user?.UserAccess;
  const isStandardUser = userAccess?.some((access) =>
    access.Role.toLowerCase().includes("standard user")
  );
  const isEaPortal = userAccess?.some((access) => access.PortalId === 2);

  const dispatch = useDispatch();
  const profileInfo = useSelector(getProfileInfo);
  const { ServiceOn } = profileInfo?.Data || initialState.profileInfo.Data;
  const [openReconnectModal, setOpenReconnectModal] = useState(false);
  const handleOpenReconnectModal = () => {
    setOpenReconnectModal(!openReconnectModal);
  };
  const emergencyHoldInfo = useSelector(getEmergencyHoldStatus);
  const {
    Jurisdiction,
    EmergencyHoldDetail,
    PledgeAmountDetail,
    OtherPledgesByAgency,
  } = emergencyHoldInfo?.Data || {};
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPledgeAmtOpen, setIsPledgeAmtOpen] = useState(false);
  const [isEHDisabledAfterRequest, setIsEHDisabledAfterRequest] = useState(false);
  const [agency, setAgency] = useState(null);

  const { control, watch, reset } = useForm({
    defaultValues: { AgencyId: null },
  });

  const AgencyId = watch("AgencyId");

  const fetchAgencyList = async () => {
    try {
      dispatch(alertActions.clear());
      const res = await dispatch(getAgencyListApi(AccountNumber)).unwrap();
      const agencyList = res?.Data?.map((data) => {
        return { label: data?.AgencyName, value: data?.AgencyId };
      });
      setAgency(agencyList);
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Pledge Amount",
        })
      );
    }
  };

  useEffect(() => {
    fetchAgencyList();
  }, [AccountNumber]);

  const {
    emergencyHoldDisabled,
    emergencyHoldTooltip,
    pledgeAmountDisabled,
    pledgeAmountTooltip,
  } = getPledgeEmergencyHoldState({
    Jurisdiction,
    EmergencyHoldDetail,
    PledgeAmountDetail,
    OtherPledgesByAgency,
    agencies: agency,
  });

  const handleOpenEmergencyHold = () => {
    setIsModalOpen(true);
  };

  const handleCloseEmergencyHold = () => {
    setIsModalOpen(false);
    reset({ AgencyId: "" });
  };

  const handleOpenPledgeAmt = () => {
    setIsPledgeAmtOpen(true);
  };

  const handleEnableEmergencyHold = async () => {
    dispatch(alertActions.clear());
    try {
      const payload = {
        AccountNumber,
        AgencyId: agency.length > 1 ? AgencyId : agency[0].value,
      };
      const res = await dispatch(getEmergencyHoldApi(payload)).unwrap();
      const { Succeeded, Data } = res;
      if (AccountNumber && Succeeded && Data.Status === "SUCCESSFUL") {
        setIsEHDisabledAfterRequest(true);
        dispatch(getEmergencyHoldStatusApi(AccountNumber)).unwrap();
        dispatch(
          alertActions.success({
            header: "Emergency Hold",
            message: Data.Message,
          }),
        );
      }
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Emergency Hold",
        }),
      );
    } finally {
      setIsModalOpen(false);
      reset({ AgencyId: null });
    }
  };

  const emergencyHoldAgencyField = (
    <Box sx={{ mt: 2 }}>
      <AutocompleteInput
        control={control}
        name="AgencyId"
        label="Select Agency"
        options={agency || []}
        forModalPopup={true}
      />
    </Box>
  );

  useEffect(() => {
    const apiRequestList = [
      dispatch(getProfileInfoApi(AccountNumber)).unwrap(),
      dispatch(getCurrentBillingApi(AccountNumber)).unwrap(),
      dispatch(getEnergyUsageApi(AccountNumber)).unwrap(),
      dispatch(getGrantInfoApi(AccountNumber)).unwrap(),
      dispatch(getBillPaymentHistoryApi(AccountNumber)).unwrap(),
      dispatch(getEmergencyHoldStatusApi(AccountNumber)).unwrap(),
    ];
    return () =>
      apiRequestList.forEach((r) => typeof r.abort === "function" && r.abort());
  }, [AccountNumber, dispatch, isEHDisabledAfterRequest]);

  return (
    <>
      <Grid container spacing={2} className="EnergyAssistanccontainer" pt={2}>
        <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
          <Profileinformation />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
          <EnergyUsage />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
          <CurrentBilling
            OpenBalances={OpenBalances}
            SecurityDepositAmountAssessedInTotal={
              SecurityDepositAmountAssessedInTotal
            }
            SecurityDepositOpenAmount={SecurityDepositOpenAmount}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <GrantInformationAndHistory />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 6 }}>
          <PaymentTransactionHistory />
        </Grid>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row", md: "row" },
            justifyContent: "space-between",
            width: "100%",
            gap: 2,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row", md: "row" },
              gap: 2,
              justifyContent: "flex-start",
              alignItems: { xs: "start", sm: "center", md: "center" },
            }}
          >
            {isEaPortal && !ServiceOn && (
              <Box>
                <Grid
                  item
                  xs={12}
                  md={6}
                  className="containedLoginbuttonleft"
                  sx={{ display: "flex", justifyContent: "flex-start" }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    className="Loginbutton"
                    color="primary"
                    onClick={handleOpenReconnectModal}
                    sx={{
                      marginLeft: {
                        xs: "0px !important",
                        md: "0px !important",
                      },
                    }}
                  >
                    Reconnect
                  </Button>
                </Grid>
              </Box>
            )}
            <Box>
              <Tooltip
                title={pledgeAmountTooltip}
                placement="top">
                <span>
                  <Button
                    type="submit"
                    variant="contained"
                    className="Loginbutton backgroundcolorred"
                    color="primary"
                    onClick={handleOpenPledgeAmt}
                    disabled={pledgeAmountDisabled}
                    sx={{
                      backgroundColor: "#0A3B70 !important",
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.26) !important",
                        color: "rgba(0, 0, 0, 0.12) !important",
                        opacity: 1,
                      },
                      marginLeft: { xs: "0px !important" },
                    }}
                  >
                    Pledge Amount
                  </Button>
                </span>
              </Tooltip>
            </Box>
            {isPledgeAmtOpen && (
              <PledgeAmountModal
                AccountNumber={AccountNumber}
                setOpenModal={setIsPledgeAmtOpen}
                Jurisdiction={Jurisdiction}
                PledgeAmountDetail={PledgeAmountDetail}
              />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row", md: "row" },
              gap: 2,
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Grid
              item
              xs={12}
              md={6}
              className="containedLoginbuttonleft"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: { xs: "100% !important", sm: "150px !important" },
              }}
            >
              <Tooltip
                title={emergencyHoldTooltip}
                placement="top"
              >
                <span>
                  <Button
                    type="submit"
                    variant="contained"
                    className="Loginbutton backgroundcolorred"
                    color="primary"
                    onClick={handleOpenEmergencyHold}
                    disabled={ emergencyHoldDisabled || isEHDisabledAfterRequest}
                    sx={{
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(0, 0, 0, 0.26) !important",
                        color: "rgba(0, 0, 0, 0.12) !important",
                        opacity: 1,
                      },
                      marginLeft: { xs: "0px !important" },
                    }}
                  >
                    Emergency Hold
                  </Button>
                </span>
              </Tooltip>
              {isModalOpen && (
                <ModalPopup
                  header="Emergency Hold"
                  message1="Are you sure you want to place emergency hold on this account?"
                  message2={agency?.length > 1 && emergencyHoldAgencyField}
                  btnPrimaryText="Confirm"
                  btnSecondaryText="Cancel"
                  isPrimaryDisable={agency?.length > 1 ? !AgencyId : false}
                  handlePrimaryClick={handleEnableEmergencyHold}
                  handleSecondaryClick={handleCloseEmergencyHold}
                />
              )}
            </Grid>
          </Box>
        </Box>
      </Grid>
      {openReconnectModal && (
        <ReconnectModal
          open={openReconnectModal}
          setOpen={setOpenReconnectModal}
        />
      )}
    </>
  );
};

export default EnergyAssistanceDetails;